import {isHierarchyLeafNode, SunburstTreeNode} from './aggregationsToSunburstTree';
import {DataLabelPosition} from 'entities/chart';
import {
    appendDataLabelValueToTreemapText,
    appendDataLabelValueToTreemapUpperLabel,
} from '@/views/storyboard/utils/chartDataLabels';

export const TREEMAP_TILE_ITEM_STYLE = {
    borderColor: '#ffffff',
    borderWidth: 0,
    gapWidth: 1,
};

export const TREEMAP_UPPER_LABEL_BACKGROUND = 'rgba(0, 0, 0, 0.35)';
export const TREEMAP_NESTED_UPPER_LABEL_BACKGROUND = 'rgba(0, 0, 0, 0.28)';

const TREEMAP_TOP_UPPER_LABEL_HEIGHT = 18;
const TREEMAP_NESTED_UPPER_LABEL_HEIGHT = 18;

const upperLabelStyle = {
    color: '#ffffff',
    align: 'left' as const,
    verticalAlign: 'middle' as const,
    padding: [0, 6],
};

type TreemapLabelParams = {
    name?: string;
    value?: number;
    data?: SunburstTreeNode;
    rect?: {
        width: number;
        height: number;
    };
    treePathInfo?: Array<{ name: string }>;
    treeAncestors?: Array<{ name: string }>;
};

function getTreemapNodeName(params: TreemapLabelParams): string {
    const name = params.name ?? '';
    if (name) {
        return name;
    }

    const path = params.treeAncestors ?? params.treePathInfo;
    return path?.[path.length - 1]?.name ?? '';
}

function getParentNodeName(params: TreemapLabelParams): string | undefined {
    const path = params.treeAncestors ?? params.treePathInfo;
    if (!path || path.length < 2) {
        return undefined;
    }

    return path[path.length - 2]?.name;
}

function isNameShownInHeader(params: TreemapLabelParams): boolean {
    const name = getTreemapNodeName(params);
    if (!name) {
        return false;
    }

    return getParentNodeName(params) === name;
}

function formatTreemapShareText(share: string): string {
    return `(${share}%)`;
}

function truncateName(name: string, maxLength: number): string {
    if (name.length <= maxLength) {
        return name;
    }

    if (maxLength <= 1) {
        return '…';
    }

    return `${name.slice(0, maxLength - 1)}…`;
}

function getTreeTotal(node: SunburstTreeNode): number {
    if (isHierarchyLeafNode(node)) {
        return node.total;
    }

    for (const child of node.children) {
        const total = getTreeTotal(child);
        if (total) {
            return total;
        }
    }

    return 0;
}

function getSharePercent(node: SunburstTreeNode | undefined, value?: number): string | undefined {
    if (!node) {
        return undefined;
    }

    if (isHierarchyLeafNode(node)) {
        return node.share;
    }

    if (value === undefined || value <= 0) {
        return undefined;
    }

    const total = getTreeTotal(node);
    if (!total) {
        return undefined;
    }

    return ((value / total) * 100).toFixed(2);
}

function getLineMaxLength(rect?: { width: number; height: number }): number {
    if (!rect) {
        return 28;
    }

    return Math.max(4, Math.floor(rect.width / 5));
}

function getSingleLineMaxLength(rect?: { width: number; height: number }): number {
    if (!rect) {
        return 28;
    }

    return Math.max(4, Math.floor(Math.min(rect.width / 5, rect.height * 1.5)));
}

function canFitTwoLines(rect?: { width: number; height: number }): boolean {
    if (!rect) {
        return true;
    }

    return rect.height >= 22;
}

function formatTreemapTileText(
    name: string,
    share: string | undefined,
    rect?: { width: number; height: number },
    nameShownInHeader = false,
): string {
    if (!share) {
        const lineMaxLength = getLineMaxLength(rect);
        return truncateName(name, lineMaxLength);
    }

    const shareText = formatTreemapShareText(share);

    if (nameShownInHeader) {
        return shareText;
    }

    const lineMaxLength = getLineMaxLength(rect);
    const displayName = truncateName(name, lineMaxLength);

    const shareLine = shareText;

    if (canFitTwoLines(rect) && shareLine.length <= lineMaxLength) {
        return `${displayName}\n${shareLine}`;
    }

    const singleLineMaxLength = getSingleLineMaxLength(rect);
    const withShare = `${name} ${shareText}`;
    if (withShare.length <= singleLineMaxLength) {
        return withShare;
    }

    const compactShare = `${name} ${share}%`;
    if (compactShare.length <= singleLineMaxLength) {
        return compactShare;
    }

    if (shareText.length <= singleLineMaxLength) {
        return shareText;
    }

    return share;
}

export function treemapTileLabelFormatter(
    params: TreemapLabelParams,
    dataLabelPosition: DataLabelPosition = DataLabelPosition.Off,
): string {
    const name = getTreemapNodeName(params);
    if (!name) {
        return appendDataLabelValueToTreemapText('', params.value, dataLabelPosition);
    }

    if (params.rect && (params.rect.width < 18 || params.rect.height < 12)) {
        return appendDataLabelValueToTreemapText('', params.value, dataLabelPosition);
    }

    const share = getSharePercent(params.data, params.value);
    const text = formatTreemapTileText(
        name,
        share,
        params.rect,
        isNameShownInHeader(params),
    );

    return appendDataLabelValueToTreemapText(text, params.value, dataLabelPosition);
}

export function treemapUpperLabelFormatter(
    params: TreemapLabelParams,
    dataLabelPosition: DataLabelPosition = DataLabelPosition.Off,
): string {
    const name = getTreemapNodeName(params);
    if (!name) {
        return '';
    }

    const maxLength = params.rect
        ? Math.max(4, Math.floor(params.rect.width / 6))
        : 32;

    return appendDataLabelValueToTreemapUpperLabel(
        truncateName(name, maxLength),
        params.value,
        dataLabelPosition,
    );
}

export function treemapNestedUpperLabelFormatter(
    params: TreemapLabelParams,
    dataLabelPosition: DataLabelPosition = DataLabelPosition.Off,
): string {
    const name = getTreemapNodeName(params);
    if (!name) {
        return '';
    }

    const maxLength = params.rect
        ? Math.max(4, Math.floor(params.rect.width / 6))
        : 32;

    return appendDataLabelValueToTreemapUpperLabel(
        truncateName(name, maxLength),
        params.value,
        dataLabelPosition,
    );
}

const baseLabelStyle = {
    color: '#ffffff',
    fontFamily: '"Rubik", sans-serif',
    textBorderWidth: 0,
    textShadowBlur: 0,
    overflow: 'truncate' as const,
    ellipsis: '…',
};

const tileLabelStyle = {
    ...baseLabelStyle,
    overflow: 'break' as const,
};

function annotateTreemapNode(
    node: SunburstTreeNode,
    dataLabelPosition: DataLabelPosition,
    depth = 0,
): SunburstTreeNode {
    if (isHierarchyLeafNode(node)) {
        return {
            ...node,
            label: {
                show: true,
                formatter: (params: TreemapLabelParams) => treemapTileLabelFormatter(params, dataLabelPosition),
            },
        };
    }

    return {
        ...node,
        upperLabel: {
            show: true,
            height: depth === 0 ? TREEMAP_TOP_UPPER_LABEL_HEIGHT : TREEMAP_NESTED_UPPER_LABEL_HEIGHT,
            padding: [0, 6],
            align: 'left',
            verticalAlign: 'middle',
            formatter: depth === 0
                ? (params: TreemapLabelParams) => treemapUpperLabelFormatter(params, dataLabelPosition)
                : (params: TreemapLabelParams) => treemapNestedUpperLabelFormatter(params, dataLabelPosition),
        },
        label: {
            show: false,
        },
        children: node.children.map((child) => annotateTreemapNode(child, dataLabelPosition, depth + 1)),
    };
}

export function annotateTreemapNodeLabels(
    nodes: SunburstTreeNode[],
    dataLabelPosition: DataLabelPosition = DataLabelPosition.Off,
): SunburstTreeNode[] {
    return nodes.map((node) => annotateTreemapNode(node, dataLabelPosition));
}

export function buildTreemapLevels(
    dataLevelCount: number,
    fontSize: number,
    dataLabelPosition: DataLabelPosition = DataLabelPosition.Off,
) {
    if (dataLevelCount <= 0) {
        return [];
    }

    const topHeaderFontSize = Math.max(11, fontSize);
    const middleHeaderFontSize = Math.max(10, fontSize - 1);
    const leafLabelFontSize = Math.max(10, fontSize - 1);

    const rootLevel = {
        itemStyle: {
            borderColor: 'transparent',
            borderWidth: 0,
            gapWidth: 0,
        },
        upperLabel: {show: false},
        label: {show: false},
    };

    const dataLevels = Array.from({length: dataLevelCount}, (_, index) => {
        const isLeafLevel = index === dataLevelCount - 1;
        const isTopLevel = index === 0;
        const isBranchLevel = !isLeafLevel;

        const upperLabelHeight = isTopLevel
            ? TREEMAP_TOP_UPPER_LABEL_HEIGHT
            : TREEMAP_NESTED_UPPER_LABEL_HEIGHT;

        return {
            itemStyle: TREEMAP_TILE_ITEM_STYLE,
            upperLabel: {
                ...baseLabelStyle,
                ...upperLabelStyle,
                show: isBranchLevel,
                height: upperLabelHeight,
                fontWeight: 500,
                fontSize: isTopLevel ? topHeaderFontSize : middleHeaderFontSize,
                lineHeight: (isTopLevel ? topHeaderFontSize : middleHeaderFontSize) + 2,
                formatter: isTopLevel
                    ? (params: TreemapLabelParams) => treemapUpperLabelFormatter(params, dataLabelPosition)
                    : (params: TreemapLabelParams) => treemapNestedUpperLabelFormatter(params, dataLabelPosition),
                backgroundColor: isTopLevel
                    ? TREEMAP_UPPER_LABEL_BACKGROUND
                    : TREEMAP_NESTED_UPPER_LABEL_BACKGROUND,
            },
            label: {
                ...tileLabelStyle,
                show: isLeafLevel,
                position: 'inside',
                align: 'center',
                verticalAlign: 'middle',
                padding: [2, 4],
                fontWeight: 400,
                fontSize: leafLabelFontSize,
                lineHeight: leafLabelFontSize + 2,
                formatter: (params: TreemapLabelParams) => treemapTileLabelFormatter(params, dataLabelPosition),
            },
        };
    });

    return [rootLevel, ...dataLevels];
}
